import { Box } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import PersistentDataGrid from 'common/PersistentDataGrid';
import * as R from 'ramda';
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import ManualDrawer from 'feature/inventory-management/reconsignments/edit/ManualDrawer';
import { format, parseISO } from 'date-fns';
import confirmationColumns from './ConfirmationColumns';

const ConfirmationPage = ({
  gridRows,
  setGridRows,
  toLocations,
  reconsignmentState,
  createReconsignmentResults,
}) => {
  const apiRef = useGridApiRef();
  const [selectedReconsignment, setSelectedReconsignment] = React.useState(null);
  const [modalMode, setModalMode] = React.useState('edit');

  const stripTimeFromDate = date => {
    if (!R.isNil(date)) {
      return parseISO(date.split('T')[0]);
    }
    return parseISO(date);
  };

  const rows = R.pipe(
    R.filter(
      R.pipe(R.prop('transferVolume'), R.both(R.complement(R.isNil), R.complement(R.isEmpty)))
    ),
    R.map(row => ({
      fromLocation: `${reconsignmentState?.fromLocation?.locationIdn} ${reconsignmentState?.fromLocation?.locationName}`,
      locationId: row.locationId,
      reconsignmentId: row.reconsignmentId,
      toLocation: `${row.locationIdName} ${row.locationName}`,
      transferDate: stripTimeFromDate(format(reconsignmentState?.transferDate, 'yyyy-MM-dd')),
      transferVolume: row.transferVolume,
    }))
  )(toLocations);

  const initialConfirmationRows = React.useMemo(
    () => R.zipWith(R.mergeDeepRight, rows, createReconsignmentResults),
    [rows, createReconsignmentResults]
  );

  useEffect(() => {
    if (R.isEmpty(gridRows)) {
      setGridRows(initialConfirmationRows);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEditClick = editId => {
    setSelectedReconsignment(editId);
    setModalMode('edit');
  };

  const handleDrawerClose = () => {
    setSelectedReconsignment(null);
  };

  // NEW: Update row directly when drawer submits
  const handleReconsignmentUpdate = updated => {
    const updatedRow = {
      fromLocation: `${updated.fromLocation.locationName}`,
      reconsignmentId: updated.reconsignmentId,
      toLocation: `${updated.toLocation.locationName}`,
      transferDate: stripTimeFromDate(updated.transferDate),
      transferVolume: updated.volume,
    };
    setGridRows(prev =>
      prev.map(row =>
        row.reconsignmentId === updatedRow.reconsignmentId
          ? {
              ...row,
              ...updatedRow, // Update the row with the new values
            }
          : row
      )
    );
    handleDrawerClose(); // Close drawer after successful update
  };

  const columns = confirmationColumns(handleEditClick);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', mb: 7, pt: 2, width: '100%' }}>
      <PersistentDataGrid
        apiRef={apiRef}
        columns={columns}
        disableColumnMenu
        getRowId={R.prop('reconsignmentId')}
        getRowSpacing={() => ({
          bottom: 0,
          top: 0,
        })}
        hideFooter
        id="confirmationGrid"
        initialState={{
          aggregation: {
            model: {
              transferVolume: 'sum',
            },
          },
        }}
        rows={gridRows}
        slots={{
          toolbar: () => <></>,
        }}
      />

      {selectedReconsignment && (
        <ManualDrawer
          modalMode={modalMode}
          onClose={handleDrawerClose}
          onReconsignmentUpdate={handleReconsignmentUpdate}
          reconsignmentInventoryOwner={reconsignmentState.inventoryOwner}
          selectedReconsignment={selectedReconsignment}
          setSelectedReconsignment={setSelectedReconsignment}
        />
      )}
    </Box>
  );
};
ConfirmationPage.propTypes = {
  createReconsignmentResults: PropTypes.shape([]).isRequired,
  gridRows: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  reconsignmentState: PropTypes.shape({
    fromLocation: PropTypes.shape({
      locationIdn: PropTypes.string,
      locationName: PropTypes.string,
    }),
    inventoryOwner: PropTypes.shape({}),
    transferDate: PropTypes.string,
  }).isRequired,
  setGridRows: PropTypes.func.isRequired,
  toLocations: PropTypes.shape([]).isRequired,
};

export default ConfirmationPage;
